<template>
    <Page>
        <Container class="max-w-screen-lg">
            <div class="pb-20">
                <Title>Marketplace</Title>

                <div class="mb-4 flex gap-2">
                    <Button :primary="type === 'template'"
                            :secondary="type !== 'template'"
                            outlined
                            size="sm"
                            @click="handleTypeChange('template')"
                    >
                        <Icon icon="list-check" class="mr-2"/>
                        Templates
                    </Button>
                    <Button :primary="type === 'movement'"
                            :secondary="type !== 'movement'"
                            outlined
                            size="sm"
                            @click="handleTypeChange('movement')"
                    >
                        <Icon icon="dumbbell" class="mr-2"/>
                        Movements
                    </Button>
                </div>

                <div class="mb-5 grid gap-3 md:grid-cols-[1fr_13rem]">
                    <div class="flex h-11 items-center rounded border border-neutral-300 px-3 dark:border-neutral-500 dark:bg-neutral-900">
                        <Icon icon="search" class="mr-2 text-neutral-500"/>
                        <input class="w-full border-0 bg-transparent p-0 text-sm outline-none focus:ring-0"
                               placeholder="Search"
                               type="search"
                               v-model="q"
                        >
                    </div>
                    <SelectInput :options="sortOptions"
                                 buttonClass="h-11 py-0"
                                 flush
                                 v-model="sort"
                    />
                </div>

                <Loading v-if="loading"/>

                <div v-else-if="!items.length" class="rounded bg-neutral-100 p-4 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                    Nothing published here yet.
                </div>

                <div v-else class="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div v-for="item in items"
                         :key="item.id"
                         :class="marketplaceCardClass(item)"
                    >
                        <div :class="marketplaceCardHeaderClass(item)">
                            <div>
                                <div class="mb-2 flex items-start justify-between gap-3">
                                    <div class="line-clamp-2 min-w-0 font-semibold leading-snug">
                                        {{ item.title }}
                                    </div>
                                    <div class="flex shrink-0 items-start gap-2">
                                        <div class="inline-flex h-6 items-center rounded border border-brand-primary-400 px-2 text-xs font-semibold text-brand-primary-500">
                                            {{ itemLabel(item) }}
                                        </div>
                                        <div v-if="item.hiddenAt"
                                             class="inline-flex h-6 items-center rounded border border-red-500 px-2 text-xs font-semibold text-red-500"
                                        >
                                            Pulled
                                        </div>
                                        <Dropdown v-if="canModerateMarketplace"
                                                  class="w-8"
                                                  dropdownClass="absolute right-0 mt-1 w-48 rounded-md bg-white py-2 text-black shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-neutral-900 dark:text-white"
                                        >
                                            <template v-slot:toggle>
                                                <button type="button"
                                                        class="flex h-6 w-8 items-center justify-center rounded border border-neutral-500 text-neutral-400 transition-colors hover:border-brand-primary-400 hover:text-brand-primary-400 focus:border-brand-primary-400 focus:text-brand-primary-400 focus:outline-none"
                                                        aria-label="Marketplace admin actions"
                                                >
                                                    <Icon icon="ellipsis-h"/>
                                                </button>
                                            </template>
                                            <template v-slot:dropdown>
                                                <button v-if="item.hiddenAt"
                                                        type="button"
                                                        class="flex w-full items-center px-3 py-2 text-left text-sm text-brand-primary-500 transition-colors hover:bg-neutral-100 disabled:cursor-wait disabled:opacity-60 dark:hover:bg-neutral-800"
                                                        :disabled="moderating[item.id]"
                                                        @click="handleRestore(item)"
                                                >
                                                    <Icon icon="check" class="mr-2"/>
                                                    Restore
                                                </button>
                                                <button v-else
                                                        type="button"
                                                        class="flex w-full items-center px-3 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-red-950/40"
                                                        :disabled="moderating[item.id]"
                                                        @click="handlePullDown(item)"
                                                >
                                                    <Icon icon="ban" class="mr-2"/>
                                                    Pull
                                                </button>
                                            </template>
                                        </Dropdown>
                                    </div>
                                </div>
                                <div class="mt-1 flex min-w-0 items-center justify-between gap-3 text-xs text-neutral-600 dark:text-neutral-300">
                                    <div class="min-w-0 truncate">
                                        {{ item.authorName }}
                                    </div>
                                    <div class="flex shrink-0 items-center gap-2">
                                        <span class="inline-flex items-center gap-1"
                                              :title="statLabel(item.viewsCount || 0, 'view')"
                                        >
                                            <Icon icon="eye" class="text-[0.68rem] text-neutral-500 dark:text-neutral-400"/>
                                            {{ item.viewsCount || 0 }}
                                        </span>
                                        <span class="inline-flex items-center gap-1"
                                              :title="statLabel(item.importsCount || 0, 'download')"
                                        >
                                            <Icon icon="download" class="text-[0.68rem] text-neutral-500 dark:text-neutral-400"/>
                                            {{ item.importsCount || 0 }}
                                        </span>
                                        <span class="inline-flex items-center gap-1"
                                              :title="ratingLabel(item)"
                                        >
                                            <Icon icon="star" class="text-[0.68rem] text-neutral-500 dark:text-neutral-400"/>
                                            {{ item.averageRating || 'New' }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-1 flex-col">
                            <div class="flex-1 space-y-3 p-3">
                                <div v-if="cardSummary(item)"
                                     class="line-clamp-3 text-sm text-neutral-700 dark:text-neutral-300"
                                >
                                    {{ item.description || cardSummary(item) }}
                                </div>

                                <div v-if="marketplaceMovementDemo(item)" class="flex">
                                    <MovementDemoVideoButton :movement="marketplaceMovementDemo(item)"
                                                             label="Watch demo"
                                    />
                                </div>

                                <div v-if="item.resourceType === 'template'" class="space-y-2">
                                    <div class="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                        Includes
                                    </div>
                                    <div class="space-y-1">
                                        <div v-for="templateItem in templateItemPreview(item)"
                                             :key="templateItem.key"
                                             class="flex items-center gap-2 text-sm"
                                        >
                                            <Icon :icon="templateItem.icon" class="shrink-0 text-xs text-brand-primary-500"/>
                                            <span class="truncate">{{ templateItem.name }}</span>
                                            <MovementDemoVideoButton :movement="templateItem.demoMovement"
                                                                     v-if="templateItem.demoMovement"
                                            />
                                        </div>
                                    </div>
                                    <button v-if="templateItemOverflow(item) > 0 || isTemplateItemsExpanded(item)"
                                            type="button"
                                            class="text-xs text-neutral-500 transition-colors hover:text-brand-primary-500 focus:text-brand-primary-500 focus:outline-none dark:text-neutral-400"
                                            :aria-expanded="isTemplateItemsExpanded(item)"
                                            @click="toggleTemplateItems(item)"
                                    >
                                        {{ templateItemToggleLabel(item) }}
                                    </button>
                                </div>
                            </div>

                            <div class="mt-auto border-t border-neutral-200 p-3 dark:border-neutral-700">
                                <div class="flex items-center justify-between gap-3">
                                    <div class="flex min-w-0 items-center gap-2">
                                        <div v-if="item.hiddenAt" class="text-xs font-semibold text-red-500">
                                            Pulled from Marketplace
                                        </div>
                                        <div v-else-if="item.userImported" class="relative flex items-center">
                                            <div class="flex items-center gap-1">
                                                <button v-for="rating in [1, 2, 3, 4, 5]"
                                                        :key="rating"
                                                        type="button"
                                                        class="p-1 text-base transition-colors focus:text-brand-primary-300 focus:outline-none disabled:cursor-wait disabled:opacity-70"
                                                        :disabled="ratingSaving[item.id]"
                                                        :class="ratingStarClass(item, rating)"
                                                        :aria-label="`Rate ${rating} ${$pluralize('star', rating)}`"
                                                        @click="handleRatingChange(item, rating)"
                                                        @mouseenter="handleRatingHover(item, rating)"
                                                        @mouseleave="handleRatingHover(item, null)"
                                                >
                                                    <Icon icon="star" :pack="ratingStarPack(item, rating)"/>
                                                </button>
                                            </div>
                                            <Loading v-if="ratingSaving[item.id]" class="ml-2 scale-50"/>
                                        </div>
                                        <div v-else-if="item.userOwnsItem" class="text-xs text-neutral-500 dark:text-neutral-400">
                                            Yours
                                        </div>
                                    </div>
                                    <div class="flex shrink-0 items-center">
                                        <Button v-if="!item.hiddenAt && !item.userImported && !item.userOwnsItem"
                                                :loading="importing[item.id]"
                                                primary
                                                size="sm"
                                                @click="handleImport(item)"
                                        >
                                            <Icon icon="download" class="mr-2"/>
                                            Import
                                        </Button>
                                        <Button v-else-if="!item.hiddenAt && (item.userImported || item.userOwnsItem)"
                                                secondary
                                                outlined
                                                size="sm"
                                                @click="handleImportedResource(item)"
                                        >
                                            <Icon icon="check" class="mr-2"/>
                                            Open
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Paginator :paginator="paginator"
                           :perPage="perPage"
                           :perPageOptions="perPageOptions"
                           @previous="handlePagePrevious"
                           @next="handlePageNext"
                           @update:perPage="handlePerPageChange"
                />
            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue'
import {
  MarketplaceItem,
} from '@/internal';
import MovementDemoVideoButton from '@/components/Movement/DemoVideoButton.vue';
import Paginator from '@/components/Paginator.vue';
import {
  capturePostHogEvent,
  getPostHogFeatureFlagValue,
  POSTHOG_FEATURE_FLAGS,
} from '@/utils/posthog';

export default defineComponent({
  name: 'Index',
  components: {
    MovementDemoVideoButton,
    Paginator,
  },
  computed: {
    canModerateMarketplace() {
      return !!this.user?.isAdmin;
    },
    items() {
      return this.$store.getters[`${MarketplaceItem.key}/collection`](this.ids);
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  created() {
    this.loadData();
  },
  data() {
    return {
      ids: [],
      importing: {},
      loading: false,
      moderating: {},
      page: 1,
      paginator: null,
      perPage: 10,
      perPageOptions: [10, 25, 50],
      q: '',
      ratingDrafts: {},
      ratingHover: {},
      ratingSaving: {},
      searchTimeout: null,
      sort: 'newest',
      sortOptions: [
        {
          label: 'Newest',
          value: 'newest',
        },
        {
          label: 'Most Viewed',
          value: 'views',
        },
        {
          label: 'Most Imported',
          value: 'imports',
        },
        {
          label: 'Highest Rated',
          value: 'rating',
        },
        {
          label: 'Title',
          value: 'title',
        },
      ],
      templateItemsExpanded: {},
      type: 'template',
    };
  },
  methods: {
    handleImportedResource(item) {
      const resourceType = item.userImportedResourceType || item.resourceType;
      const resourceUuid = item.userImportedResourceUuid || item.resourceUuid;

      if (!resourceType || !resourceUuid) {
        return;
      }

      const routeType = resourceType === 'template' ? 'template' : 'movement';
      this.$router.push(`/${routeType}/${resourceUuid}/edit`);
    },
    handleImport(item) {
      this.importing[item.id] = true;

      this.$store.dispatch(`${MarketplaceItem.key}/import`, {
        id: item.id,
      }).then(() => {
        this.$store.dispatch('ui/snackbar', {
          message: 'Imported.',
          type: 'success',
        });
        this.captureMarketplaceEvent('marketplace_item_imported', item);
        this.loadData(this.page, true);
      }).catch((error) => {
        this.$store.dispatch('ui/snackbar', {
          message: error.response?.data?.message || 'Unable to import.',
          type: 'danger',
        });
      }).finally(() => {
        this.importing[item.id] = false;
      });
    },
    handlePageNext() {
      this.loadData(this.page + 1);
    },
    handlePagePrevious() {
      this.loadData(this.page - 1);
    },
    handlePerPageChange(perPage) {
      this.perPage = perPage;
      this.loadData(1);
    },
    handlePullDown(item) {
      if (!window.confirm(`Pull "${item.title}" from the Marketplace?`)) {
        return;
      }

      this.moderating[item.id] = true;

      this.$store.dispatch(`${MarketplaceItem.key}/pullDown`, {
        id: item.id,
      }).then(() => {
        this.$store.dispatch('ui/snackbar', {
          message: 'Marketplace item pulled down.',
          type: 'success',
        });
        this.captureMarketplaceEvent('marketplace_item_pulled', item);
        return this.loadData(this.page, true);
      }).catch((error) => {
        this.$store.dispatch('ui/snackbar', {
          message: error.response?.data?.message || 'Unable to pull item down.',
          type: 'danger',
        });
      }).finally(() => {
        this.moderating[item.id] = false;
      });
    },
    handleRestore(item) {
      if (!window.confirm(`Restore "${item.title}" to the Marketplace?`)) {
        return;
      }

      this.moderating[item.id] = true;

      this.$store.dispatch(`${MarketplaceItem.key}/restore`, {
        id: item.id,
      }).then(() => {
        this.$store.dispatch('ui/snackbar', {
          message: 'Marketplace item restored.',
          type: 'success',
        });
        this.captureMarketplaceEvent('marketplace_item_restored', item);
        return this.loadData(this.page, true);
      }).catch((error) => {
        this.$store.dispatch('ui/snackbar', {
          message: error.response?.data?.message || 'Unable to restore item.',
          type: 'danger',
        });
      }).finally(() => {
        this.moderating[item.id] = false;
      });
    },
    handleRatingChange(item, rating) {
      const previousRating = this.currentRating(item);

      this.setRatingDraft(item, rating);
      this.setRatingSaving(item, true);

      this.$store.dispatch(`${MarketplaceItem.key}/rate`, {
        id: item.id,
        rating,
      }).then(() => {
        this.$store.dispatch('ui/snackbar', {
          message: 'Rating saved.',
          type: 'success',
        });
        this.captureMarketplaceEvent('marketplace_item_rated', item, {
          rating,
        });
      }).catch((error) => {
        this.$store.dispatch('ui/snackbar', {
          message: error.response?.data?.message || 'Unable to save rating.',
          type: 'danger',
        });
        this.setRatingDraft(item, previousRating);
      }).finally(() => {
        this.setRatingSaving(item, false);
      });
    },
    handleRatingHover(item, rating) {
      this.ratingHover = {
        ...this.ratingHover,
        [item.id]: rating,
      };
    },
    handleTypeChange(type) {
      this.type = type;
      this.loadData(1);
    },
    cardSummary(item) {
      if (item.resourceType === 'template') {
        return item.description || null;
      }

      const tracking = [
        item.snapshot?.resource?.track_repetitions ? 'reps' : null,
        item.snapshot?.resource?.track_repetitions_partial ? 'partials' : null,
        item.snapshot?.resource?.track_resistance ? 'resistance' : null,
        item.snapshot?.resource?.track_duration ? 'duration' : null,
      ].filter(Boolean);

      return tracking.length ? `Tracks ${tracking.join(', ')}.` : 'Ready to import into your movements.';
    },
    itemLabel(item) {
      return item.resourceType === 'template' ? 'Template' : 'Movement';
    },
    marketplaceEventProperties(extra = {}) {
      return {
        page: this.page,
        posthog_feature_flag: POSTHOG_FEATURE_FLAGS.marketplace,
        posthog_feature_flag_value: getPostHogFeatureFlagValue(POSTHOG_FEATURE_FLAGS.marketplace),
        query_present: Boolean(this.q),
        resource_type: this.type,
        sort: this.sort,
        ...extra,
      };
    },
    captureMarketplaceEvent(event, item = null, extra = {}) {
      capturePostHogEvent(event, this.marketplaceEventProperties({
        item_id: item?.id || null,
        item_resource_type: item?.resourceType || null,
        item_title: item?.title || null,
        ...extra,
      }));
    },
    marketplaceMovementDemo(item) {
      if (item.resourceType !== 'movement') {
        return null;
      }

      return this.snapshotDemoMovement(item.snapshot?.resource, item.title);
    },
    marketplaceCardClass(item) {
      return [
        'flex h-full flex-col overflow-hidden rounded border bg-neutral-100 dark:bg-neutral-800',
        item.hiddenAt
          ? 'border-red-500/60 opacity-75'
          : 'border-transparent',
      ];
    },
    marketplaceCardHeaderClass(item) {
      return [
        'p-3',
        item.hiddenAt
          ? 'bg-red-950/20 dark:bg-red-950/30'
          : 'bg-neutral-200 dark:bg-neutral-700',
      ];
    },
    ratingLabel(item) {
      return item.averageRating ? `${item.averageRating} rating` : 'New rating';
    },
    snapshotDemoMovement(snapshot, fallbackName = 'Movement') {
      const demoVideoUrl = snapshot?.demoVideoUrl || snapshot?.demo_video_url;

      if (!demoVideoUrl) {
        return null;
      }

      return {
        demoVideoUrl,
        name: snapshot?.name || fallbackName,
      };
    },
    currentRating(item) {
      return this.ratingDrafts[item.id]?.rating ?? item.userRating?.rating ?? 0;
    },
    previewRating(item) {
      return this.ratingHover[item.id] ?? this.currentRating(item);
    },
    ratingStarClass(item, rating) {
      return this.previewRating(item) >= rating
        ? 'text-brand-primary-400'
        : 'text-neutral-500 hover:text-brand-primary-300';
    },
    ratingStarPack(item, rating) {
      return this.previewRating(item) >= rating ? 'fas' : 'fal';
    },
    setRatingDraft(item, rating) {
      this.ratingDrafts = {
        ...this.ratingDrafts,
        [item.id]: {
          rating,
        },
      };
    },
    setRatingSaving(item, saving) {
      this.ratingSaving = {
        ...this.ratingSaving,
        [item.id]: saving,
      };
    },
    statLabel(value, singular) {
      const count = Number(value) || 0;

      return `${count} ${this.$pluralize(singular, count)}`;
    },
    isTemplateItemsExpanded(item) {
      return !!this.templateItemsExpanded[item.id];
    },
    templateItemOverflow(item) {
      return Math.max(0, this.templateItems(item).length - 4);
    },
    templateItemPreview(item) {
      const items = this.templateItems(item);

      return this.isTemplateItemsExpanded(item) ? items : items.slice(0, 4);
    },
    templateItemToggleLabel(item) {
      if (this.isTemplateItemsExpanded(item)) {
        return 'Show less';
      }

      return `+${this.templateItemOverflow(item)} more`;
    },
    templateItems(item) {
      const dependencies = item.snapshot?.dependencies?.movements || [];
      const templateItems = item.snapshot?.resource?.items || [];
      const consumedSetIds = new Set();

      return templateItems
        .map((candidate, index) => {
          if (candidate.type === 'rest_timer') {
            return {
              icon: 'stopwatch',
              key: candidate.key || `rest-timer-${index}`,
              name: `${candidate.label || 'Rest Timer'} (${this.$timerValue(candidate.duration || 90)})`,
            };
          }

          const movementId = candidate.movementId || candidate.movement_id;
          const dependency = dependencies.find((item) => item.movementId === movementId || item.movement_id === movementId);
          const setId = candidate.setId || candidate.set_id;

          if (setId) {
            if (consumedSetIds.has(setId)) {
              return null;
            }

            consumedSetIds.add(setId);
          }

          const setCount = setId
            ? templateItems.filter((item) => {
              return item.type === 'movement' && (item.setId || item.set_id) === setId;
            }).length
            : 1;

          return {
            demoMovement: this.snapshotDemoMovement(dependency?.snapshot, dependency?.snapshot?.name || 'Movement'),
            icon: 'dumbbell',
            key: setId ? `set-${setId}` : `${movementId}-${index}`,
            name: `${dependency?.snapshot?.name || 'Movement'}${setCount > 1 ? ` x${setCount}` : ''}`,
          };
        })
        .filter(Boolean);
    },
    toggleTemplateItems(item) {
      this.templateItemsExpanded = {
        ...this.templateItemsExpanded,
        [item.id]: !this.isTemplateItemsExpanded(item),
      };
    },
    loadData(page = 1, inBackground = false) {
      this.page = page;
      this.loading = !inBackground;

      return this.$store.dispatch(`${MarketplaceItem.key}/index`, {
        params: {
          page,
          perPage: this.perPage,
          q: this.q,
          sort: this.sort,
          type: this.type,
        },
      }).then(({ids, paginator}) => {
        this.ids = ids;
        this.paginator = paginator;

        if (!inBackground) {
          capturePostHogEvent('marketplace_viewed', this.marketplaceEventProperties({
            result_count: ids.length,
          }));
        }
      }).finally(() => {
        this.loading = false;
      });
    },
  },
  watch: {
    q() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadData(1);
      }, 250);
    },
    sort() {
      this.loadData(1);
    },
  },
})
</script>

<style scoped lang="scss">

</style>
